import { Link } from "react-router-dom";
import { BRAND, CATEGORIES } from "@/lib/constants";
import { Mail, MapPin, MessageCircle, Phone, Linkedin, Facebook, Instagram } from "lucide-react";

// Inline X (Twitter) glyph — lucide's Twitter icon still shows the old bird
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SOCIALS = [
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/equipmenthub-india/",
    Icon: Linkedin,
    bg: "#0A66C2",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/equipmenthubindiacom",
    Icon: Instagram,
    bg: "linear-gradient(135deg,#f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1DeHcG1t3s/?mibextid=wwXIfr",
    Icon: Facebook,
    bg: "#1877F2",
  },
  {
    name: "X",
    href: "#",
    comingSoon: true,
    Icon: XIcon,
    // X (Twitter) brand blue — matches attached reference
    bg: "#1DA1F2",
  },
];

export default function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 mt-24" data-testid="site-footer">
      <div className="mx-auto w-full max-w-[1700px] px-4 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src={BRAND.logoUrl} alt="EquipmentHub India" className="h-16 w-16 object-contain" style={{ mixBlendMode: "screen" }} />
            <span className="font-display font-black text-white text-lg">
              EquipmentHub<span className="text-ehi-accent">.</span>India
            </span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            {BRAND.footerText}
          </p>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Mail className="w-4 h-4 text-ehi-accent" />
              <a href={`mailto:${BRAND.contactEmail}`} className="hover:text-white transition-colors">
                {BRAND.contactEmail}
              </a>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Phone className="w-4 h-4 text-ehi-accent" />
              <a href={`tel:+${BRAND.whatsappNumber}`} className="hover:text-white transition-colors">
                {BRAND.whatsappDisplay}
              </a>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <MessageCircle className="w-4 h-4 text-ehi-accent" />
              <a href={`https://wa.me/${BRAND.whatsappNumber}`} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
                WhatsApp: {BRAND.whatsappDisplay}
              </a>
            </div>
            <div className="flex items-start gap-2 text-slate-400">
              <MapPin className="w-4 h-4 text-ehi-accent mt-0.5 shrink-0" />
              <span data-testid="footer-head-office" className="whitespace-pre-line">{`Registered Head Office:
Office -504, 2nd Floor, ILD Trade Centre
Sector 47, Gurugram, Haryana 122018, India`}</span>
            </div>
          </div>

          {/* Social icons — professional brand-tile row */}
          <div className="mt-6">
            <div className="label-eyebrow text-slate-500 mb-3">Follow us</div>
            <div className="flex items-center gap-2.5" data-testid="footer-socials">
              {SOCIALS.map(({ name, href, Icon, bg, comingSoon }) => {
                const commonCls = "relative w-10 h-10 grid place-items-center rounded-sm text-white shadow-[0_2px_6px_rgba(0,0,0,0.25)] ring-1 ring-white/10 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ehi-accent";
                const style = { background: bg };
                const title = comingSoon ? `${name} — coming soon` : name;
                const inner = (
                  <>
                    <Icon className="w-[18px] h-[18px]" />
                    {comingSoon && (
                      <span
                        aria-hidden
                        className="absolute -top-1.5 -right-1.5 text-[8px] font-black tracking-wider uppercase bg-ehi-accent text-white rounded-sm px-1 py-[1px] leading-none shadow-sm"
                        style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
                      >
                        Soon
                      </span>
                    )}
                  </>
                );
                if (comingSoon) {
                  return (
                    <button
                      key={name}
                      type="button"
                      aria-label={title}
                      title={title}
                      data-testid={`footer-social-${name.toLowerCase()}`}
                      className={commonCls}
                      style={style}
                      onClick={(e) => e.preventDefault()}
                    >
                      {inner}
                    </button>
                  );
                }
                return (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={title}
                    title={title}
                    data-testid={`footer-social-${name.toLowerCase()}`}
                    className={commonCls}
                    style={style}
                  >
                    {inner}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          <h4 className="label-eyebrow text-slate-500 mb-4">Marketplace</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/marketplace?type=rental" className="hover:text-white transition-colors">Rental Marketplace</Link></li>
            <li><Link to="/buy-new" className="hover:text-white transition-colors">Buy New Equipment</Link></li>
            <li><Link to="/workforce" className="hover:text-white transition-colors">Workforce</Link></li>
            <li><Link to="/inspection" className="hover:text-white transition-colors">Inspection & Certification</Link></li>
            <li><Link to="/post-requirement" className="hover:text-white transition-colors">Post Requirement</Link></li>
            <li><Link to="/become-supplier" className="hover:text-white transition-colors">Become a Supplier</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="label-eyebrow text-slate-500 mb-4">Categories</h4>
          <ul className="space-y-2 text-sm">
            {CATEGORIES.slice(0, 6).map((c) => (
              <li key={c.id}>
                <Link to={`/marketplace?category=${c.id}`} className="hover:text-white transition-colors">
                  {c.name} Rental India
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="label-eyebrow text-slate-500 mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
            <li><Link to="/pricing" className="hover:text-white transition-colors">Membership Plans</Link></li>
            <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            <li><Link to="/login" className="hover:text-white transition-colors">Log in</Link></li>
            <li><Link to="/careers" className="hover:text-white transition-colors" data-testid="footer-careers">Careers</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 bg-slate-900/60">
        <div className="mx-auto w-full max-w-[1700px] px-4 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> 5,000+ Verified Suppliers</span>
          <span className="hidden sm:inline text-slate-700">·</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> 100+ Cities Pan India</span>
          <span className="hidden sm:inline text-slate-700">·</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> KYC Checked · GST on File</span>
          <span className="hidden sm:inline text-slate-700">·</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Lead Protection Enabled</span>
          <span className="hidden sm:inline text-slate-700">·</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> EHI-Managed Communication</span>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="mx-auto w-full max-w-[1700px] px-4 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} EquipmentHub India. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <a href="/supplier-agreement" className="hover:text-white transition-colors">Supplier Agreement</a>
            <a href="/mission" className="hover:text-white transition-colors">Mission</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/refund-policy" className="hover:text-white transition-colors">Refund Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
